import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../services/collateralApi';
import { MachineryType, LegalRegime, MAX_OWNERS } from '../types/Machinery';
import type { MachineryOwner, MachineryCost } from '../types/Machinery';
import type { ErrorField } from '../types/ErrorField';

export default function useMachineryPageForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
    const [isCreating, setIsCreating] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);

    // Machinery fields
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [machineryType, setMachineryType] = useState<MachineryType | ''>('');
    const [legalRegime, setLegalRegime] = useState<LegalRegime | ''>('');
    const [invoiceValue, setInvoiceValue] = useState<number | ''>('');
    const [custodyAddress, setCustodyAddress] = useState('');
    const [custodyCity, setCustodyCity] = useState('');
    const [custodyState, setCustodyState] = useState('');
    const [rtdRegistryOffice, setRtdRegistryOffice] = useState('');
    const [registryOffice, setRegistryOffice] = useState('');
    const [registryState, setRegistryState] = useState('');
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [brand, setBrand] = useState('');
    const [model, setModel] = useState('');
    const [yearManufacture, setYearManufacture] = useState<number | ''>('');
    const [yearModel, setYearModel] = useState<number | ''>('');
    const [serialNumber, setSerialNumber] = useState('');
    const [finameCode, setFinameCode] = useState('');
    const [color, setColor] = useState('');
    const [comments, setComments] = useState('');

    // Obligation (optional in create)
    const [creditOperationId, setCreditOperationId] = useState('');
    const [obligations, setObligations] = useState<any[]>([]);
    const [isLoadingObligations, setIsLoadingObligations] = useState(false);

    // Owners (multiple)
    const [owners, setOwners] = useState<MachineryOwner[]>([]);

    // Owner search (candidate owner before adding to the list)
    const [ownerSearch, setOwnerSearch] = useState('');
    const [ownerResults, setOwnerResults] = useState<any[]>([]);
    const [isSearchingOwner, setIsSearchingOwner] = useState(false);
    const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);

    // Costs (optional)
    const [costs, setCosts] = useState<MachineryCost[]>([]);

    const addOwner = (owner: MachineryOwner) => {
        // Validate against the current state BEFORE calling the updater, so no
        // side effect (toast) runs inside setOwners. React 18 StrictMode can
        // invoke the functional updater more than once in dev, which would
        // fire duplicate toasts if the check lived inside it.
        if (owners.length >= MAX_OWNERS) {
            toast.error(`Máximo de ${MAX_OWNERS} proprietários permitido`);
            return;
        }
        if (owner.personId && owners.some(o => o.personId === owner.personId)) {
            toast.error('Proprietário já adicionado');
            return;
        }
        setOwners(prev => [...prev, owner]);
    };
    const removeOwner = (index: number) => setOwners(prev => prev.filter((_, i) => i !== index));

    const addCost = (cost: MachineryCost) => setCosts(prev => [...prev, cost]);
    const removeCost = (index: number) => setCosts(prev => prev.filter((_, i) => i !== index));

    // Load obligations on mount
    useEffect(() => {
        const loadObligations = async () => {
            setIsLoadingObligations(true);
            try {
                const result = await collateralApi.getObligations({ page: 1, pageSize: 500 });
                setObligations(result.items || []);
            } catch {
                setObligations([]);
            } finally {
                setIsLoadingObligations(false);
            }
        };
        loadObligations();
    }, []);

    const searchOwner = async (query: string) => {
        if (!query || query.length < 3) {
            setOwnerResults([]);
            setShowOwnerDropdown(false);
            return;
        }
        setIsSearchingOwner(true);
        try {
            const [legalResults, individualResults] = await Promise.all([
                collateralApi.searchLegalPerson(query).catch(() => []),
                collateralApi.searchPerson(query).catch(() => []),
            ]);

            const normalized = [
                ...legalResults.map((p: any) => ({
                    personId: p.personId || p.uuid || p.id || '',
                    name: p.legalName || p.fantasyName || p.name || '',
                    document: (p.govTaxId || '').replace(/\D/g, ''),
                    documentFormatted: p.govTaxIdFormatted || p.govTaxId || '',
                    type: 'PJ',
                })),
                ...individualResults.map((p: any) => ({
                    personId: p.id || p.personId || '',
                    name: p.name || p.fullName || '',
                    document: (p.socialId || '').replace(/\D/g, ''),
                    documentFormatted: p.socialId || '',
                    type: 'PF',
                })),
            ];

            setOwnerResults(normalized);
            setShowOwnerDropdown(normalized.length > 0);
        } catch {
            setOwnerResults([]);
        } finally {
            setIsSearchingOwner(false);
        }
    };

    const handleSelectOwner = (person: any) => {
        addOwner({
            personId: person.personId,
            name: person.name,
            document: person.document,
        });
        setOwnerSearch('');
        setOwnerResults([]);
        setShowOwnerDropdown(false);
    };

    const validate = (): ErrorField[] => {
        const errors: ErrorField[] = [];
        if (!description.trim()) {
            errors.push({ fieldName: 'description', message: 'Campo obrigatório' });
        }
        if (owners.length < 1) {
            errors.push({ fieldName: 'owners', message: 'Informe ao menos um proprietário' });
        }
        if (owners.length > MAX_OWNERS) {
            errors.push({ fieldName: 'owners', message: `Máximo de ${MAX_OWNERS} proprietários permitido` });
        }
        const hasInvalidDocument = owners.some(o => {
            const digits = (o.document || '').replace(/\D/g, '');
            return digits.length !== 11 && digits.length !== 14;
        });
        if (hasInvalidDocument) {
            errors.push({ fieldName: 'owners', message: 'Proprietário com CPF/CNPJ inválido' });
        }
        return errors;
    };

    const handleCreate = async () => {
        const validationErrors = validate();
        if (validationErrors.length > 0) {
            setErrorFields(validationErrors);
            toast.error('Há campos obrigatórios!');
            return;
        }
        setIsCreating(true);
        setErrorFields([]);
        try {
            await collateralApi.createMachinery({
                description,
                machinery_type: machineryType || undefined,
                legal_regime: legalRegime || undefined,
                invoice_value: invoiceValue === '' ? undefined : invoiceValue,
                custody_address: custodyAddress || undefined,
                custody_city: custodyCity || undefined,
                custody_state: custodyState ? custodyState.toUpperCase() : undefined,
                rtd_registry_office: rtdRegistryOffice || undefined,
                registry_office: registryOffice || undefined,
                registry_state: registryState ? registryState.toUpperCase() : undefined,
                registration_number: registrationNumber || undefined,
                brand: brand || undefined,
                model: model || undefined,
                year_manufacture: yearManufacture === '' ? undefined : yearManufacture,
                year_model: yearModel === '' ? undefined : yearModel,
                serial_number: serialNumber || undefined,
                finame_code: finameCode || undefined,
                color: color || undefined,
                name: name || undefined,
                comments: comments || undefined,
                owners: owners.map(o => ({
                    person_id: o.personId,
                    name: o.name,
                    document: o.document,
                })),
                costs: costs.map(c => ({
                    cost_type: c.costType,
                    value: c.value,
                    cost_date: c.costDate,
                    registration_number: c.registrationNumber,
                    registration_date: c.registrationDate,
                    description: c.description,
                })),
                credit_operation_id: creditOperationId || undefined,
            });
            onCreated();
            onClose();
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Há campos obrigatórios!');
                setErrorFields(e.data.errors);
            } else if (e instanceof ApiError && e.status === 409) {
                toast.error(e.data?.message || e.message);
            } else {
                toast.error(e?.message || 'Erro ao cadastrar maquinário');
            }
        } finally {
            setIsCreating(false);
        }
    };

    return {
        isCreating, errorFields,
        name, setName,
        description, setDescription,
        machineryType, setMachineryType,
        legalRegime, setLegalRegime,
        invoiceValue, setInvoiceValue,
        custodyAddress, setCustodyAddress,
        custodyCity, setCustodyCity,
        custodyState, setCustodyState,
        rtdRegistryOffice, setRtdRegistryOffice,
        registryOffice, setRegistryOffice,
        registryState, setRegistryState,
        registrationNumber, setRegistrationNumber,
        brand, setBrand,
        model, setModel,
        yearManufacture, setYearManufacture,
        yearModel, setYearModel,
        serialNumber, setSerialNumber,
        finameCode, setFinameCode,
        color, setColor,
        comments, setComments,
        creditOperationId, setCreditOperationId,
        obligations, isLoadingObligations,
        owners, addOwner, removeOwner,
        ownerSearch, setOwnerSearch,
        ownerResults, isSearchingOwner, showOwnerDropdown,
        searchOwner, handleSelectOwner,
        costs, addCost, removeCost,
        handleCreate,
    };
}
